/**
 * Populates a template with variables.
 *
 * The variables in the template should be in the format `{# variable #}`.
 * For example, if you have a variable named `foo`, you would use it in the template like this: `{# foo #}`.
 *
 * Usage example (from an existing `<template>` element):
 * ```html
 * <template id="product-pill-template">
 *   <a class="pill" href="{# href #}">
 *     {# label #}
 *   </a>
 * </template>
 * ```
 * ```js
 * import { populateTemplate } from '~/scripts/utils/populate-template.js';
 *
 * const templateEl = document.getElementById('product-pill-template');
 * const fragment = populateTemplate(templateEl, {
 *   href: '/collections/all',
 *   label: 'All products',
 * });
 *
 * document.querySelector('[data-pill-list]').appendChild(fragment);
 * ```
 *
 * Usage example (multiple variables + attributes):
 * ```html
 * <template id="menu-item-template">
 *   <a href="{# href #}" class="menu-item {# activeClass #}">
 *     {# label #}
 *   </a>
 * </template>
 * ```
 * ```js
 * const fragment = populateTemplate(
 *   document.getElementById('menu-item-template'),
 *   { href: '/sale', activeClass: 'is-active', label: 'Sale' },
 * );
 * ```
 *
 * Security note: `populateTemplate` writes the result via `innerHTML`, so values are treated as HTML.
 * Only pass trusted strings (or pre-escape/sanitize untrusted input).
 *
 * @param {HTMLTemplateElement} template - The template to populate.
 * @param {Object} variables - An object containing the variables to populate the template with.
 * @returns {DocumentFragment} - The populated template as a DocumentFragment.
 */
export const populateTemplate = (template, variables) => {
    if (!template || !(template instanceof HTMLTemplateElement)) {
        throw new Error('Invalid template element');
    }
    if (typeof variables !== 'object' || variables === null) {
        throw new Error('Invalid variables object');
    }

    const tempDiv = document.createElement('div');
    tempDiv.appendChild(template.content.cloneNode(true));

    tempDiv.innerHTML = tempDiv.innerHTML.replace(
        /{#\s*(.*?)\s*#}/g,
        (match, variableName) => variables[variableName] || ''
    );

    const clone = document.createDocumentFragment();
    while (tempDiv.firstChild) {
        clone.appendChild(tempDiv.firstChild);
    }

    return clone;
};
